import React, { useState, useMemo, useEffect } from 'react';
import { SnackbarProvider } from 'notistack';
import { useLocation } from "react-router-dom";

import { createMuiTheme, MuiThemeProvider } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';

import AppLoading from 'app/components/loaders/AppLoading';
import ManageQueryErrors from 'app/components/ManageQueryErrors';

import AppContext from 'app/context/AppContext';
import useApi from 'app/context/useApi';
import TerminalProvider from 'app/context/TerminalProvider';

import AppConnect from 'app/AppConnect';
import AppLayout from 'app/AppLayout';

// Init App Variables
const initialApp = {
    club: null,
    user: null,
    meta: null
}

export default function AppProvider() {

    const [ app, setApp ] = useState(initialApp);
    const [ appLoading, setAppLoading ] = useState(true);

    let location = useLocation();
    const api = useApi();

    const hydrateApp = (quiet = false) => {

        if (! quiet) setAppLoading(true);

        api.get('/hydrate')
            .then(res => {
                setApp({...app, user: res.app.user, club: res.app.club});

                if (res.app.club) {
                    document.title = res.app.club.name;
                    document.getElementsByTagName('meta').namedItem('description').setAttribute('content', res.app.club.description);

                    if (res.app.club.branding.icon) document.getElementById("favicon").href = res.app.club.branding.icon.full_url;
                }
            }).finally(() => setAppLoading(false));
    }

    const appTheme = useMemo(() => createMuiTheme({
        palette: {
            primary: {
                main: process.env.MIX_PRIMARY_COLOR
            },
            secondary: {
                main: app.club ? app.club.branding.primary_color : process.env.MIX_SECONDARY_COLOR,
            },
        },
        typography: {
            h1: {
                fontSize: '3.75rem'
            },
            h2: {
                fontSize: '2.75rem'
            },
            h3: {
                fontSize: '2.25rem'
            },
            h4: {
                fontSize: '1.7rem'
            }
        },
        width: {
            container: 1340,
            navigation: 240,
            adminNavigation: 320
        },
        mixins: {
            toolbar: {
                minHeight: 52
            }
        }
    }), [app]);

    const handleApp = (keyString, value, persist = false) => {
        let newApp = {...app};
        const keys = keyString.split('.');

        keys.reduce((o,p,i) => o[p] = keyString.split('.').length === ++i ? value : o[p] || {}, newApp);

        setApp(newApp)

        if (persist) api.post('/profile/state', {application_state: newApp.user.application_state});
    }

    const handlePageView = (location) => {
        const data = {
            pathname: location.pathname,
            search: location.search,
            hash: location.hash
        }

        api.post('/track/page', data);
    }

    useEffect(() => {
        handlePageView(location);
    }, [location])

    return (
        <AppContext.Provider value={{ app, handleApp, hydrateApp }}>
            <MuiThemeProvider theme={ appTheme }>
                <SnackbarProvider maxSnack={4}>
                    <AppLoading loading={appLoading} />
                    <TerminalProvider>
                        <AppConnect>
                            <ManageQueryErrors />
                            <CssBaseline />
                            <AppLayout appLoading={appLoading}/>
                        </AppConnect>
                    </TerminalProvider>
                    <CssBaseline />
                </SnackbarProvider>
            </MuiThemeProvider>
        </AppContext.Provider>
    )
}
